import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { NAV_ITEMS, ROUTES } from '@/config/site'
import { useScrollLock } from '@/hooks/useScrollLock'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'

import { LanguageToggle } from './LanguageToggle'
import { Wordmark } from './Wordmark'

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'relative text-xs font-medium uppercase tracking-[0.16em] transition-colors duration-200',
    isActive ? 'text-gold' : 'text-muted hover:text-parchment',
  )
}

export function SiteHeader() {
  const { t } = useI18n()
  const location = useLocation()
  const [isMenuOpen, setMenuOpen] = useState(false)
  const [isScrolled, setScrolled] = useState(() => window.scrollY > 16)

  useScrollLock(isMenuOpen)

  // Any navigation closes the mobile panel. Adjusting state during render is
  // React's documented pattern for "reset state when a value changes" — it
  // avoids the extra commit an effect would cause.
  const [renderedPath, setRenderedPath] = useState(location.pathname)
  if (renderedPath !== location.pathname) {
    setRenderedPath(location.pathname)
    setMenuOpen(false)
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!isMenuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isMenuOpen])

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        isScrolled || isMenuOpen
          ? 'border-b border-gold/10 bg-ink/85 backdrop-blur-lg'
          : 'border-b border-transparent',
      )}
    >
      <div className="gutter mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 lg:h-20">
        <Wordmark />

        {/* Desktop navigation */}
        <nav className="hidden items-center gap-8 lg:flex" aria-label={t.nav.menu}>
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === ROUTES.home} className={navLinkClass}>
              {t.nav[item.labelKey]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle />
          <Link
            to={ROUTES.join}
            className="hidden min-h-10 items-center rounded-full bg-gold px-5 text-xs font-medium uppercase tracking-[0.16em] text-ink transition hover:bg-gold-light sm:inline-flex"
          >
            {t.nav.join}
          </Link>

          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
            aria-label={isMenuOpen ? t.nav.close : t.nav.open}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold lg:hidden"
          >
            <span className="relative block h-3.5 w-5">
              <span
                className={cn(
                  'absolute left-0 h-px w-full bg-current transition-transform duration-300',
                  isMenuOpen ? 'top-1/2 rotate-45' : 'top-0',
                )}
              />
              <span
                className={cn(
                  'absolute left-0 top-1/2 h-px w-full bg-current transition-opacity duration-200',
                  isMenuOpen && 'opacity-0',
                )}
              />
              <span
                className={cn(
                  'absolute left-0 h-px w-full bg-current transition-transform duration-300',
                  isMenuOpen ? 'top-1/2 -rotate-45' : 'bottom-0',
                )}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div
        id="mobile-nav"
        hidden={!isMenuOpen}
        className="gutter h-[calc(100dvh-4rem)] overflow-y-auto border-t border-gold/10 bg-ink pb-[max(2rem,env(safe-area-inset-bottom))] pt-2 lg:hidden"
      >
        <nav aria-label={t.nav.menu} className="mx-auto flex max-w-7xl flex-col">
          {[...NAV_ITEMS, { to: ROUTES.join, labelKey: 'join' as const }].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === ROUTES.home}
              className={({ isActive }) =>
                cn(
                  'border-b border-line/60 py-4 font-serif text-xl transition-colors',
                  isActive ? 'text-gold' : 'text-parchment hover:text-gold',
                )
              }
            >
              {t.nav[item.labelKey]}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  )
}
